import { cls } from "@wss/cls";
import { createElement, ExtendedImgHTMLAttributes } from "react";
export * from "./image-host";
type DefaultProps = ExtendedImgHTMLAttributes<Omit<HTMLImageElement, "width" | "height">>;
type ImageProps = DefaultProps & {
  width: `${number}` | number;
  height: `${number}` | number;
};

/**
 * An <img> component with required width and height properties.
 *
 * **Props - Width & Height**
 *
 * The width and height properties tell the browser what size of the image will be once rendered allowing it to reserve the space needed to display the image. See {@link https://web.dev/articles/serve-images-with-correct-dimensions#avoid_layout_shifts_by_specifying_dimensions Googles article} on images for more information.
 *
 * **Customizing Fallback**
 *
 * When the image fails to load a white fallback is shown over the image via the `after:` CSS pseudo property. This can be customized to be any color via the `after:bg-[COLOR]` tailwind classes.
 *
 * @example
 * ```jsx
 * <Image sr="/bad/url.jpg" className="after:bg-green-500" />
 * ```
 */
export function Image(props: ImageProps): JSX.Element {
  return createElement("img", {
    ...props,
    className: cls("relative after:bg-white", props.className, "overflow-hidden after:content-[''] after:w-full after:h-full after:absolute after:top-0 after:left-0 after:block")
  });
}